import React, { useState, useEffect } from "react";
import { getFormConfig, getFieldsForStep, getStepTitle, getTotalSteps, validateStep, isFieldVisible, getDynamicOptions } from "../services/formConfigService";
import "./DynamicForm.css";

/**
 * DynamicForm Component
 * Renders form fields dynamically based on configuration
 * Supports multiple form types: registration_pet_owner, registration_provider, contact_us
 */
const DynamicForm = ({
  formType = "registration_pet_owner",
  onSubmit,
  submitButtonText = "Submit",
  showStepper = true,
  initialData = {},
  apiConfig = null // Optional API config for fetching dynamic configs
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [formConfig, setFormConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [termsScrollStates, setTermsScrollStates] = useState({});
  const [showPassword, setShowPassword] = useState({});

  const totalSteps = formConfig ? formConfig.steps.length : getTotalSteps(formType);
  const currentFields = formConfig 
    ? (formConfig.steps.find(s => s.stepNumber === currentStep)?.fields || [])
    : getFieldsForStep(formType, currentStep);
  const stepTitle = formConfig
    ? (formConfig.steps.find(s => s.stepNumber === currentStep)?.stepTitle || '')
    : getStepTitle(formType, currentStep);

  // Fetch form configuration from API if provided
  useEffect(() => {
    const fetchFormConfig = async () => {
      if (apiConfig) {
        setIsLoading(true);
        try {
          const response = await fetch(apiConfig.url, {
            headers: {
              'Authorization': `Bearer ${apiConfig.token}`,
              'Content-Type': 'application/json',
            }
          });
          const data = await response.json();
          if (data.success && data.data) {
            setFormConfig(data.data);
          }
        } catch (error) {
          console.error("Error fetching form config:", error);
          // Fallback to default config
          setFormConfig(getFormConfig(formType));
        } finally {
          setIsLoading(false);
        }
      } else {
        setFormConfig(getFormConfig(formType));
      }
    };

    fetchFormConfig();
  }, [formType, apiConfig]);

  const handleInputChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: null
      }));
    }
  };

  const handleNext = () => {
    const { isValid, errors: stepErrors } = validateStep(currentFields, formData);
    if (!isValid) {
      setErrors(stepErrors);
      return;
    }
    setErrors({});
    if (currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setErrors({});
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate all steps
    let allErrors = {};
    let isFormValid = true;

    if (formConfig) {
      formConfig.steps.forEach(step => {
        const { isValid, errors: stepErrors } = validateStep(step.fields, formData);
        if (!isValid) {
          allErrors = { ...allErrors, ...stepErrors };
          isFormValid = false;
        }
      });
    } else {
      for (let step = 1; step <= totalSteps; step++) {
        const fields = getFieldsForStep(formType, step);
        const { isValid, errors: stepErrors } = validateStep(fields, formData);
        if (!isValid) {
          allErrors = { ...allErrors, ...stepErrors };
          isFormValid = false;
        }
      }
    }

    if (!isFormValid) {
      setErrors(allErrors);
      return;
    }

    setErrors({});
    onSubmit(formData);
  };

  const handleTermsScroll = (fieldName, e) => {
    const element = e.target;
    const scrollPercentage = (element.scrollTop + element.clientHeight) / element.scrollHeight;
    if (scrollPercentage >= 0.95) {
      setTermsScrollStates(prev => ({ ...prev, [fieldName]: true }));
    }
  };

  const handleTermsCheckboxChange = (fieldName, checked) => {
    if (checked) {
      handleInputChange(fieldName, [fieldName]);
    } else {
      handleInputChange(fieldName, []);
    }
  };

  const renderField = (field) => {
    // Skip rendering if field is not visible (conditional field)
    if (!isFieldVisible(field, formData)) {
      return null;
    }

    const fieldError = errors[field.name];
    const value = formData[field.name] || (field.type === 'checkbox' || field.type === 'radio' ? [] : '');
    switch (field.type) {
     case 'text':
case 'email':
case 'password':
case 'tel':
case 'number':
case 'date':
  return (
    <div key={field.name} className="input_container">
      <label>
        {field.label}
        {field.required && <span className="required">*</span>}
      </label>

      <div className="password-wrapper">
        <input
          type={
            field.type === 'password'
              ? (showPassword[field.name] ? 'text' : 'password')
              : field.type
          }
          className={`form-control custom-input ${fieldError ? 'is-invalid' : ''}`}
          placeholder={field.placeholder}
          value={value}
          onChange={(e) => handleInputChange(field.name, e.target.value)}
        />

        {field.type === 'password' && (
          <button
            type="button"
            className="toggle-password"
            onClick={() =>
              setShowPassword((prev) => ({
                ...prev,
                [field.name]: !prev[field.name],
              }))
            }
          >
            <i
              className={`fa-solid ${
                showPassword[field.name] ? "fa-eye-slash" : "fa-eye"
              }`}
            ></i>
          </button>
        )}
      </div>

      {fieldError && (
        <div className="invalid-feedback">{fieldError}</div>
      )}
    </div>
  );

      case 'textarea':
        return (
          <div key={field.name} className="input_container">
            <label>{field.label} {field.required && <span className="required">*</span>}</label>
            <textarea
              rows="3"
              className={`form-control custom-input ${fieldError ? 'is-invalid' : ''}`}
              placeholder={field.placeholder}
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
            />
            {fieldError && <div className="invalid-feedback">{fieldError}</div>}
          </div>
        );

      case 'select':
        const selectOptions = getDynamicOptions(field, formData);
        return (
          <div key={field.name} className="input_container">
            <label>{field.label} {field.required && <span className="required">*</span>}</label>
            <select
              className={`form-select custom-input ${fieldError ? 'is-invalid' : ''}`}
              value={value}
              onChange={(e) => {
                handleInputChange(field.name, e.target.value);
                // Clear dependent fields when pet type changes
                if (field.name === 'pet_type') {
                  handleInputChange('breed', '');
                  handleInputChange('other_breed', '');
                }
              }}
            >
              <option value="" disabled>Select {field.label.toLowerCase()}</option>
              {selectOptions?.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            {fieldError && <div className="invalid-feedback">{fieldError}</div>}
          </div>
        );

     case 'checkbox': {
  const currentValues = Array.isArray(value) ? value : [];

  // Checkbox Group
  if (field.options && field.options.length > 0) {
    const maxSelections = field.maxSelections;
    const isMaxReached =
      maxSelections && currentValues.length >= maxSelections;

    return (
      <div key={field.name} className="mb-3">
        <label className="form-label">
          {field.label}
          {field.required && <span className="required">*</span>}

          {maxSelections && (
            <span className="text-muted ms-2">
              (Select up to {maxSelections})
            </span>
          )}
        </label>

        <div className="selection-grid">
          {field.options.map((option) => {
            const isChecked = currentValues.includes(option.value);
            const isDisabled = !isChecked && isMaxReached;

            return (
              <label
                key={option.value}
                className={`select-card ${
                  isChecked ? "active" : ""
                } ${isDisabled ? "disabled" : ""}`}
              >
                <input
                  type="checkbox"
                  className={fieldError ? "is-invalid" : ""}
                  checked={isChecked}
                  disabled={isDisabled}
                  onChange={() => {
                    const updatedValues = isChecked
                      ? currentValues.filter((v) => v !== option.value)
                      : [...currentValues, option.value];

                    handleInputChange(field.name, updatedValues);
                  }}
                />

                <span>{option.label}</span>
              </label>
            );
          })}
        </div>

        {fieldError && (
          <div className="invalid-feedback d-block">
            {fieldError}
          </div>
        )}
      </div>
    );
  }

  // Single Checkbox
  const isChecked = currentValues.includes(field.name);

  return (
    <div key={field.name} className="mb-3">
      <label
        className={`mb-0 single-checkbox ${
          isChecked ? "active" : ""
        }`}
      >
        <input
        style={{marginRight:10}}
          type="checkbox"
          className={fieldError ? "is-invalid" : ""}
          checked={isChecked}
          onChange={(e) => {
            const updatedValues = e.target.checked
              ? [...currentValues, field.name]
              : currentValues.filter((v) => v !== field.name);

            handleInputChange(field.name, updatedValues);
          }}
        />

        <span>
          {field.label}
          {field.required && (
            <span className="required"> *</span>
          )}
        </span>
      </label>

      {fieldError && (
        <div className="invalid-feedback d-block">
          {fieldError}
        </div>
      )}
    </div>
  );
}

      case 'radio':
        return (
          <div key={field.name} className="input_container">
            <label>{field.label} {field.required && <span className="required">*</span>}</label>
            <div className="selection-grid">
              {field.options?.map(option => (
                <label className="select-card" key={option.value}>
                  <input
                    type="radio"
                    name={field.name}
                    value={option.value}
                    checked={value === option.value}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
            {fieldError && <div className="text-danger mt-1">{fieldError}</div>}
          </div>
        );

      case 'file':
        return (
          <div key={field.name} className="input_container">
            <label>{field.label} {field.required && <span className="required">*</span>}</label>
            <input
              type="file"
              className="form-control custom-input"
              placeholder={field.placeholder}
              onChange={(e) => handleInputChange(field.name, e.target.files[0])}
            />
            {fieldError && <div className="invalid-feedback d-block">{fieldError}</div>}
          </div>
        );

      case 'terms_popup': {
        const hasScrolled = termsScrollStates[field.name];
        const isTermsChecked = Array.isArray(value) && value.includes(field.name);
        
        const handleOpenPdf = () => {
          window.open(field.popupPdfUrl, '_blank', 'noopener,noreferrer');
        };
        
        return (
          <div key={field.name} className="mb-3 terms-popup-field">
            <button
              type="button"
              className="btn btn-outline-primary btn-sm mb-2"
              onClick={handleOpenPdf}
            >
              📄 Read {field.popupTitle}
            </button>
            <p className="text-muted small mb-2">Click the button above to open the document in a new tab. Please read it completely.</p>
            
            <div className="form-check">
              <input
                type="checkbox"
                className={`form-check-input ${fieldError ? 'is-invalid' : ''}`}
                id={field.name}
                checked={isTermsChecked}
                disabled={!hasScrolled}
                onChange={(e) => handleTermsCheckboxChange(field.name, e.target.checked)}
              />
              <label className="form-check-label" htmlFor={field.name}>
                {field.label} {field.required && <span className="required">*</span>}
                {!hasScrolled && <span className="text-muted ms-2">(Please read the document first)</span>}
              </label>
              {fieldError && <div className="invalid-feedback d-block">{fieldError}</div>}
            </div>

            {/* Terms Modal */}
            <div className="modal fade" id={`${field.name}Modal`} tabIndex="-1" aria-hidden="true">
              <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{field.popupTitle}</h5>
                    <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                  </div>
                  <div
                    className="modal-body terms-modal-body"
                    onScroll={(e) => handleTermsScroll(field.name, e)}
                  >
                    <div className="text-center py-4">
                      <h5>Please read the {field.popupTitle}</h5>
                      <p className="text-muted mb-4">
                        Click the button below to open the document in a new tab.
                        After reading it completely, scroll down to acknowledge.
                      </p>
                      <button
                        type="button"
                        className="btn btn-primary btn-lg"
                        onClick={handleOpenPdf}
                      >
                        📄 Open {field.popupTitle} in New Tab
                      </button>
                    </div>
                    <div className="text-center mt-3 p-3 bg-light rounded">
                      <p className="mb-2 text-muted">After reading the document, scroll down to acknowledge.</p>
                      <div className="form-check d-inline-block">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`${field.name}Ack`}
                          checked={hasScrolled}
                          disabled
                        />
                        <label className="form-check-label" htmlFor={`${field.name}Ack`}>
                          I have scrolled to the bottom
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  if (isLoading) {
    return <div className="text-center py-5">Loading form...</div>;
  }

  return (
    <form onSubmit={handleSubmit} className="dynamic-form">
      {showStepper && (
        <div className="stepper-progress mb-4">
          <div className="d-flex justify-content-between mb-2">
            <span className="steps_txt">
              Step {currentStep} of {totalSteps}
            </span>
            <span className="steps_p">
              {Math.round((currentStep / totalSteps) * 100)}%
            </span>
          </div>
          <div className="progress">
            <div
              className="progress-bar"
              style={{
                width: `${(currentStep / totalSteps) * 100}%`,
              }}
            ></div>
          </div>
        </div>
      )}

      {stepTitle && showStepper && (
        <h4 className="step-title mb-4">{stepTitle}</h4>
      )}

      <div className="form-fields">
        {currentFields.map(field => renderField(field))}
      </div>

      <div className="d-flex justify-content-between mt-4">
        {currentStep > 1 ? (
          <button
            type="button"
            className="btn back_btn w-100 me-3"
            onClick={handleBack}
          >
            Back
          </button>
        ) : (
          <div></div>
        )}

        {currentStep < totalSteps ? (
          <button
            type="button"
            className="btn login-btn w-100"
            onClick={handleNext}
          >
            Next
          </button>
        ) : (
          <button
            type="submit"
            className="btn login-btn w-100"
          >
            {submitButtonText}
          </button>
        )}
      </div>
    </form>
  );
};

export default DynamicForm;
